<template>
  <v-row>
    <v-col cols="6">
      <!-- Measure and KPI selection -->
      <v-card class="fill-height" variant="outlined" color="primary">
        <v-tabs v-model="selectedTab">
          <v-tab
            v-for="t in tabs"
            :key="t"
            :text="t"
            :value="t"
            :prepend-icon="t === 'measures' ? 'mdi-gauge' : 'mdi-ruler'"
          >
            {{ $t(`labels.${t}`) }}
          </v-tab>
        </v-tabs>
        <!-- Hint -->
        <v-card-subtitle class="pt-2 text-grey-darken-3 text-truncate">
          {{ $t("strings.arrow-button-hint") }}
        </v-card-subtitle>
        <!-- Search -->
        <v-card-text>
          <v-text-field
            ref="searchField"
            v-model="searchText"
            :label="$t('labels.search')"
            variant="outlined"
            hide-details
            single-line
            density="compact"
          ></v-text-field>
        </v-card-text>
        <v-tabs-window v-model="selectedTab">
          <v-tabs-window-item v-for="t in tabs" :key="t" :value="t">
            <v-divider></v-divider>
            <v-virtual-scroll
              v-if="(t === 'measures' ? measuresList : kpisList).length"
              :key="scrollKey"
              :items="t === 'measures' ? measuresList : kpisList"
              item-height="64"
              :height="itemsVirtualHeight"
            >
              <template v-slot:default="{ item }">
                <v-divider></v-divider>
                <v-list-item :key="item.name">
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle
                    class="d-inline-block text-truncate w-100"
                  >
                    {{ item.description }}
                  </v-list-item-subtitle>
                  <template v-slot:append>
                    <v-list-item-action>
                      <v-btn
                        color="primary"
                        variant="plain"
                        icon="mdi-arrow-right-bold-circle"
                        @click="addItem(item)"
                      ></v-btn>
                    </v-list-item-action>
                  </template>
                </v-list-item>
              </template>
            </v-virtual-scroll>
            <v-sheet v-else :min-height="itemsVirtualHeight">
              <v-card-subtitle
                v-if="t === 'measures' && measuresList.length <= 0"
                class="pt-2 text-grey-darken-3 d-inline-block text-truncate w-100"
              >
                {{ $t("strings.no-measures-items") }}
              </v-card-subtitle>
              <v-card-subtitle
                v-else-if="t === 'kpis' && kpisList.length <= 0"
                class="pt-2 text-grey-darken-3 d-inline-block text-truncate w-100"
              >
                {{ $t("strings.no-kpis-items") }}
              </v-card-subtitle>
            </v-sheet>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card>
    </v-col>
    <v-col cols="6">
      <!-- END Measure and KPI selection -->
      <!-- List of selected items -->
      <v-card class="mx-1 fill-height">
        <v-toolbar :flat="true" color="secondary">
          <v-toolbar-title class="text-white">
            {{ $t("labels.selected") }}
            <div class="text-body-2">
              {{ $t("strings.selected-items-hint") }}
            </div>
          </v-toolbar-title>
          <template v-slot:append>
            <v-tooltip activator="parent" location="left">
              <span>{{ $t("labels.delete-all") }}</span>
            </v-tooltip>
            <v-btn
              variant="plain"
              @click="clearItems()"
              icon="mdi-delete-sweep"
            ></v-btn>
          </template>
        </v-toolbar>
        <v-virtual-scroll
          v-if="selectedItems.length"
          id="rightScroll"
          :items="selectedItems"
          item-height="64"
          :height="selectedVirtualHeight"
        >
          <template v-slot:default="{ item }">
            <v-list-item :key="item.name">
              <template v-slot:prepend>
                <v-icon color="primary darken-4">
                  {{ checkIcon(item) }}
                </v-icon>
              </template>
              <v-list-item-title>
                {{ item.name }}
              </v-list-item-title>
              <template v-slot:append>
                <v-list-item-action>
                  <v-tooltip activator="parent" location="left">
                    <span>{{ $t("labels.delete") }}</span>
                  </v-tooltip>
                  <v-btn
                    variant="plain"
                    @click="removeItem(item)"
                    icon="mdi-delete"
                    color="secondary"
                  ></v-btn>
                </v-list-item-action>
              </template>
            </v-list-item>
            <v-divider></v-divider>
          </template>
        </v-virtual-scroll>
        <v-sheet v-else>
          <v-card-subtitle class="pt-2 text-grey-darken-3 text-truncate">
            {{ $t("strings.no-selected-items") }}
          </v-card-subtitle>
        </v-sheet>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from "axios";
import events from "@/mixins/events.js";
import utils from "@/mixins/utils.js";

export default {
  name: "kpiMeasuresComponent",
  mixins: [events, utils],
  inheritAttrs: false,
  props: [
    "apiUrl",
    "realm",
    "parentLoading",
    "parentSelected",
    "parentDefinition",
    "containerHeight",
  ],
  emits: ["updateSelectedItem", "toggleOverlay"],
  data() {
    return {
      loading: false,
      scrollKey: 0, // To force re-render of virtual-scroll
      tabs: ["measures", "kpis"],
      selectedTab: null,
      searchText: "",
      measures: [],
      kpis: [],
      tabsList: {
        measure: [],
        kpi: [],
      },
      selectedItems: [],
      listHeaderDefault: {
        type: "none",
        name: this.$t("labels.empty"),
        icon: "mdi-cancel",
      },
    };
  },
  async mounted() {
    this.$nextTick(() => {
      this.selectedItems =
        JSON.parse(sessionStorage.getItem("kpi"))?.definition?.selected || [];
    });

    try {
      this.loading = true;
      await this.getMeasures();
      await this.getKPIsList();
    } finally {
      this.excludeSelectedItems();
      this.loading = false;
    }
  },
  computed: {
    itemsVirtualHeight() {
      return `calc(${this.containerHeight} - ${parseFloat(this.containerHeight) / 4 + 16}px - 24px - 148px - 60px)`;
    },
    selectedVirtualHeight() {
      return `calc(${this.containerHeight} - ${parseFloat(this.containerHeight) / 4 + 16}px - 24px - 64px - 60px)`;
    },
    searchResults() {
      return (items) => {
        return this.searchText
          ? items.filter((item) =>
              item.name.toLowerCase().includes(this.searchText.toLowerCase()),
            )
          : items;
      };
    },
    measuresList() {
      return this.searchResults(this.tabsList.measure);
    },
    kpisList() {
      return this.searchResults(this.tabsList.kpi);
    },
  },

  watch: {
    tabsList: {
      handler(value) {
        this.tabsList = value;
        // Non assegnare direttamente a measuresList o kpisList
      },
      deep: true,
    },
    selectedItems(value) {
      this.$emit("updateSelectedItem", value);
    },
    loading(value) {
      this.$emit("toggleOverlay", value);
    },
    searchText() {
      // Trigger reactivity when searchText changes
      this.$nextTick(() => {
        this.measuresList;
        this.kpisList;
      });
    },
  },
  methods: {
    // Removes the selected items from their respective original lists
    excludeSelectedItems() {
      const selectedItemsId = new Set(
        this.selectedItems.map((item) => item.id),
      );
      // Excludes items's IDs present in selectedItemsId from the arrays tabsList.measure and tabsList.kpi
      this.tabsList.measure = this.tabsList.measure.filter(
        (measure) => !selectedItemsId.has(measure.id),
      );
      this.tabsList.kpi = this.tabsList.kpi.filter(
        (kpi) => !selectedItemsId.has(kpi.id),
      );
    },

    checkIcon(item) {
      return item.type == "kpi" ? "mdi-gauge" : "mdi-ruler";
    },

    async getKPIsList() {
      try {
        const response = await axios.get(`${this.apiUrl}/${this.realm}/kpi`, {
          withCredentials: true,
        });
        this.kpis = response.data.data;
        this.tabsList.kpi = this.kpis.map((item) => ({
          id: item.id,
          type: "kpi",
          name: item.name,
          description: item.description,
        }));
        this.sortList("kpi");
      } catch (err) {
        this.setError(err, false);
      }
    },

    async getMeasures() {
      try {
        const response = await axios.get(
          `${this.apiUrl}/${this.realm}/measures`,
          { withCredentials: true },
        );
        this.measures = response.data.data;
        this.tabsList.measure = this.measures.map((item) => ({
          id: item.id,
          type: "measure",
          name: item.name,
          description: item.description,
        }));
        this.sortList("measure");
      } catch (err) {
        this.setError(err, false);
      }
    },

    addItem(item) {
      this.selectedItems = [...this.selectedItems, item];
      this.filterList(item);
      this.updateScrollKey(); // Update scrollKey to force re-render of virtual-scroll
    },

    removeItem(item) {
      this.selectedItems = this.selectedItems.filter(
        (selectedItem) => selectedItem.id !== item.id,
      );
      this.addToOriginalList(item);
      this.updateScrollKey(); // Update scrollKey
    },
    filterList(item) {
      this.tabsList[item.type] = this.tabsList[item.type].filter(
        (i) => i.id !== item.id,
      );
    },
    clearItems() {
      this.selectedItems.forEach((item) => {
        this.addToOriginalList(item);
      });
      this.selectedItems = [];
      this.updateScrollKey(); // Update scrollKey
    },

    addToOriginalList(item) {
      if (!this.tabsList[item.type].some((i) => i.id === item.id)) {
        this.tabsList[item.type].push(item);
      }
      this.sortList(item.type);
    },

    updateScrollKey() {
      this.scrollKey += 1; // Increment scrollKey to force update
    },
    sortList(type) {
      this.tabsList[type].sort((a, b) => a.name.localeCompare(b.name));
    },
  },
};
</script>
